import { useState } from "react";

import type { EntityVersionAnalyticsQuerySnapshot, EntityVersionAnalyticsVariant } from "./types";

import { AnalyticsQueryProvider } from "@/components/RequestsAnalytics/analytics-query-context";
import { ANALYTICS_TAB_INDEX } from "@/components/RequestsAnalytics/constants";
import { RequestsAnalyticsHeaderControls } from "@/components/RequestsAnalytics/RequestsAnalyticsPageHeader";
import RequestsAnalyticsSearchbar from "@/components/RequestsAnalytics/RequestsAnalyticsSearchbar";
import RequestsAnalyticsTabs from "@/components/RequestsAnalytics/RequestsAnalyticsTabs";
import { RequestLogsTableRefreshProvider } from "@/components/RequestsAnalytics/request-logs-table-refresh-context";
import { StructuredSearchProvider } from "@/components/StructuredSearch";
import { tracesSearchSchema } from "@/components/StructuredSearch/schemas/traces-search-schema";
import { TracesTableRefreshProvider } from "@/components/TracesSearch/TracesAgGrid/traces-table-refresh-context";
import { TracesAnalyticsHeaderControls } from "@/components/TracesSearch/TracesAnalyticsPageHeader";
import TracesAnalyticsTabs from "@/components/TracesSearch/TracesAnalyticsTabs";
import TracesSearchbar from "@/components/TracesSearch/TracesSearchbar";
import { useApplyOlderDataConstraints } from "@/routes/request-logs/search/hooks/useApplyOlderDataConstraints";
import { requestsSearchSchema } from "@/routes/request-logs/search/requests-search-schema";

interface EntityVersionAnalyticsSheetContentProps {
  querySnapshot: EntityVersionAnalyticsQuerySnapshot;
  variant: EntityVersionAnalyticsVariant;
}

const RequestsSheetInnerContent = ({
  querySnapshot,
}: {
  querySnapshot: EntityVersionAnalyticsQuerySnapshot;
}) => {
  useApplyOlderDataConstraints();
  const [selectedTabIndex, setSelectedTabIndex] = useState(ANALYTICS_TAB_INDEX);

  return (
    <AnalyticsQueryProvider>
      <RequestLogsTableRefreshProvider>
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between gap-3">
            <div className="text-lg font-semibold text-gray-900">Filters</div>
            <RequestsAnalyticsHeaderControls selectedTabIndex={selectedTabIndex} />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <RequestsAnalyticsSearchbar />
            <RequestsAnalyticsTabs
              selectedTabIndex={selectedTabIndex}
              lockedFilters={querySnapshot.lockedFilters}
              initialDateRange={querySnapshot.dateRange}
              onSelectedTabChange={setSelectedTabIndex}
              syncUrl={false}
            />
          </div>
        </div>
      </RequestLogsTableRefreshProvider>
    </AnalyticsQueryProvider>
  );
};

const TracesSheetInnerContent = () => {
  useApplyOlderDataConstraints();
  const [selectedTabIndex, setSelectedTabIndex] = useState(ANALYTICS_TAB_INDEX);

  return (
    <AnalyticsQueryProvider>
      <TracesTableRefreshProvider>
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between gap-3">
            <div className="text-lg font-semibold text-gray-900">Filters</div>
            <TracesAnalyticsHeaderControls selectedTabIndex={selectedTabIndex} />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <TracesSearchbar />
            <TracesAnalyticsTabs
              selectedTabIndex={selectedTabIndex}
              onSelectedTabChange={setSelectedTabIndex}
              syncUrl={false}
            />
          </div>
        </div>
      </TracesTableRefreshProvider>
    </AnalyticsQueryProvider>
  );
};

const EntityVersionAnalyticsSheetContent = ({
  querySnapshot,
  variant,
}: EntityVersionAnalyticsSheetContentProps) => (
  <StructuredSearchProvider
    config={variant === "traces" ? tracesSearchSchema : requestsSearchSchema}
    initialLockedFilters={querySnapshot.lockedFilters}
    initialEditableFilters={querySnapshot.editableFilters}
    initialQ={querySnapshot.q}
    initialDateRange={querySnapshot.dateRange}
    initialUrlHydrationReady
  >
    {variant === "traces" ? (
      <TracesSheetInnerContent />
    ) : (
      <RequestsSheetInnerContent querySnapshot={querySnapshot} />
    )}
  </StructuredSearchProvider>
);

export default EntityVersionAnalyticsSheetContent;
